import type { TypedSupabaseClient } from '../client';
import type {
  IAppConfigRepository,
  AppConfigTenantRow,
  AppConfigUserRow,
} from '../../../interfaces/src';

// Backs the App Builder (web) and the mobile app config bootstrap.
// Schema: see supabase/migrations/20260502120000_mobile_app_config.sql
//
// Validation: this layer trusts whatever JSON it receives — the caller is
// expected to have validated against @erp/app-builder/appConfigSchema first.
// We never look inside the JSON here; that lives in libs/app-builder.

interface RawTenantRow {
  tenant_id: string;
  version: number;
  status: 'draft' | 'published';
  config: unknown;
  previous_config: unknown | null;
  published_at: string | null;
  updated_at: string;
}

interface RawUserRow {
  user_id: string;
  tenant_id: string;
  override: unknown;
  updated_at: string;
}

function toTenantRow(r: RawTenantRow): AppConfigTenantRow {
  return {
    tenantId: r.tenant_id,
    version: r.version,
    status: r.status,
    config: r.config,
    previousConfig: r.previous_config,
    publishedAt: r.published_at,
    updatedAt: r.updated_at,
  };
}

function toUserRow(r: RawUserRow): AppConfigUserRow {
  return {
    userId: r.user_id,
    tenantId: r.tenant_id,
    override: r.override,
    updatedAt: r.updated_at,
  };
}

async function currentTenantId(supabase: TypedSupabaseClient): Promise<string> {
  // current_user_tenant_id() is the same RPC every other tenant-scoped repo
  // uses. Returns NULL for users who haven't completed onboarding — the
  // mobile app handles that by routing to the wizard.
  const { data, error } = await supabase.rpc('current_user_tenant_id' as never);
  if (error) throw error;
  if (!data) throw new Error('No tenant context');
  return data as unknown as string;
}

export class SupabaseAppConfigRepository implements IAppConfigRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getTenantPublished(): Promise<AppConfigTenantRow | null> {
    const { data, error } = await this.supabase
      .from('app_config_tenant' as never)
      .select('*')
      .eq('status', 'published')
      .maybeSingle();
    if (error) throw error;
    return data ? toTenantRow(data as unknown as RawTenantRow) : null;
  }

  async getTenantLatest(): Promise<AppConfigTenantRow | null> {
    // Single row per tenant; RLS already filters to the caller's tenant.
    const { data, error } = await this.supabase
      .from('app_config_tenant' as never)
      .select('*')
      .maybeSingle();
    if (error) throw error;
    return data ? toTenantRow(data as unknown as RawTenantRow) : null;
  }

  async saveTenantDraft(config: unknown): Promise<AppConfigTenantRow> {
    const tenantId = await currentTenantId(this.supabase);
    const { data: { user } } = await this.supabase.auth.getUser();

    const { data, error } = await this.supabase
      .from('app_config_tenant' as never)
      .upsert(
        {
          tenant_id: tenantId,
          status: 'draft',
          config,
          updated_by: user?.id ?? null,
        } as never,
        { onConflict: 'tenant_id' },
      )
      .select('*')
      .single();
    if (error) throw error;
    return toTenantRow(data as unknown as RawTenantRow);
  }

  async publishTenant(config: unknown): Promise<AppConfigTenantRow> {
    const tenantId = await currentTenantId(this.supabase);
    const { data: { user } } = await this.supabase.auth.getUser();

    // Read current row to preserve previous_config for rollback.
    const current = await this.getTenantLatest();
    const previousConfig =
      current?.status === 'published' ? current.config : current?.previousConfig ?? null;
    const nextVersion = (current?.version ?? 0) + 1;

    const { data, error } = await this.supabase
      .from('app_config_tenant' as never)
      .upsert(
        {
          tenant_id: tenantId,
          status: 'published',
          config,
          previous_config: previousConfig,
          version: nextVersion,
          published_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        } as never,
        { onConflict: 'tenant_id' },
      )
      .select('*')
      .single();
    if (error) throw error;
    return toTenantRow(data as unknown as RawTenantRow);
  }

  async rollbackTenant(): Promise<AppConfigTenantRow> {
    const current = await this.getTenantLatest();
    if (!current?.previousConfig) {
      throw new Error('No previous config to roll back to');
    }
    return this.publishTenant(current.previousConfig);
  }

  async getUserOverride(): Promise<AppConfigUserRow | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await this.supabase
      .from('app_config_user' as never)
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data ? toUserRow(data as unknown as RawUserRow) : null;
  }

  async setUserOverride(override: unknown): Promise<AppConfigUserRow> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const tenantId = await currentTenantId(this.supabase);
    const { data, error } = await this.supabase
      .from('app_config_user' as never)
      .upsert(
        {
          user_id: user.id,
          tenant_id: tenantId,
          override,
        } as never,
        { onConflict: 'user_id' },
      )
      .select('*')
      .single();
    if (error) throw error;
    return toUserRow(data as unknown as RawUserRow);
  }

  async clearUserOverride(): Promise<void> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) return;
    const { error } = await this.supabase
      .from('app_config_user' as never)
      .delete()
      .eq('user_id', user.id);
    if (error) throw error;
  }

  async seedDefaultIfMissing(): Promise<AppConfigTenantRow> {
    const { data, error } = await this.supabase.rpc(
      'seed_default_app_config_for_current_user' as never,
    );
    if (error) throw error;
    return toTenantRow(data as unknown as RawTenantRow);
  }
}
